import { ArrowUpRight } from 'lucide-react';
import { type ComponentProps, memo, useMemo } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remend from 'remend';

/*
 * Ответ агента в Markdown (T074; contracts/ui.md §2; FR-035): `react-markdown` 10 + `remark-gfm`.
 * - HTML не рисуется (`skipHtml`), картинок нет (`img` запрещён): внешних запросов из ответа нет. Адреса ссылок чистит
 *   `react-markdown` (`javascript:` и другие опасные схемы — прочь). Внешняя ссылка — в новой вкладке, со стрелкой ↗.
 * - Ответ растёт по мере вывода: текст делится на абзацы (вне блоков кода), устоявшийся абзац — тот же `Block` с тем же
 *   текстом и не перерисовывается; дописывается только последний. Незакрытую разметку хвоста (`**жир`, `[ссылка](`)
 *   достраивает `remend`, пока ответ идёт.
 * - Широкая таблица и блок кода прокручиваются внутри себя: лента вбок не прокручивается.
 */

const PLUGINS = [remarkGfm];
const NO = ['img'];

function isExternal(href: string | undefined): boolean {
  if (!href) return false;
  try {
    return new URL(href, location.href).origin !== location.origin;
  } catch {
    return false;
  }
}

function Link({ href, children, node: _n, ...rest }: ComponentProps<'a'> & { node?: unknown }) {
  const external = isExternal(href);
  return (
    <a
      {...rest}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="text-accent underline underline-offset-2 hover:no-underline"
    >
      {children}
      {external && (
        <ArrowUpRight size={12} strokeWidth={1.75} className="ml-0.5 inline align-[-1px]" aria-hidden />
      )}
    </a>
  );
}

const COMPONENTS: Components = {
  a: Link,
  p: ({ node: _n, ...p }) => <p {...p} className="break-words" />,
  ul: ({ node: _n, ...p }) => <ul {...p} className="list-disc space-y-0.5 pl-5" />,
  ol: ({ node: _n, ...p }) => <ol {...p} className="list-decimal space-y-0.5 pl-5" />,
  h1: ({ node: _n, ...p }) => <p {...p} className="text-[15px] font-semibold" />,
  h2: ({ node: _n, ...p }) => <p {...p} className="text-[15px] font-semibold" />,
  h3: ({ node: _n, ...p }) => <p {...p} className="text-[14px] font-semibold" />,
  h4: ({ node: _n, ...p }) => <p {...p} className="font-semibold" />,
  strong: ({ node: _n, ...p }) => <strong {...p} className="font-semibold" />,
  blockquote: ({ node: _n, ...p }) => (
    <blockquote {...p} className="border-l-2 border-line pl-3 text-ink-2" />
  ),
  code: ({ node: _n, className, ...p }) => (
    <code {...p} className={`rounded-[4px] bg-subtle px-1 font-mono text-[12px] ${className ?? ''}`} />
  ),
  pre: ({ node: _n, ...p }) => (
    <pre
      {...p}
      className="overflow-x-auto rounded-control border border-line bg-subtle p-3 font-mono text-[12px] [&>code]:bg-transparent [&>code]:p-0"
    />
  ),
  table: ({ node: _n, ...p }) => (
    <div className="max-w-full overflow-x-auto">
      <table {...p} className="border-collapse text-[13px] tabular-nums" />
    </div>
  ),
  th: ({ node: _n, ...p }) => (
    <th {...p} className="border border-line bg-subtle px-2 py-1 text-left font-semibold" />
  ),
  td: ({ node: _n, ...p }) => <td {...p} className="border border-line px-2 py-1" />,
  hr: () => <hr className="border-line" />,
};

/** Абзацы ответа: граница — пустая строка вне блока кода. */
export function splitBlocks(text: string): string[] {
  const out: string[] = [];
  let cur: string[] = [];
  let fence: string | null = null;
  for (const line of text.split('\n')) {
    const m = /^\s*(```+|~~~+)/u.exec(line);
    if (m?.[1]) {
      const mark = m[1].slice(0, 3);
      if (fence === null) fence = mark;
      else if (fence === mark) fence = null;
    }
    if (fence === null && line.trim() === '') {
      if (cur.length > 0) out.push(cur.join('\n'));
      cur = [];
    } else cur.push(line);
  }
  if (cur.length > 0) out.push(cur.join('\n'));
  return out;
}

const Block = memo(function Block({ text }: { text: string }) {
  return (
    <Markdown remarkPlugins={PLUGINS} skipHtml disallowedElements={NO} components={COMPONENTS}>
      {text}
    </Markdown>
  );
});

/** Ответ агента: абзацы по одному; пока ответ идёт, хвост достраивает `remend`. */
export function MarkdownText({ text, streaming = false }: { text: string; streaming?: boolean }) {
  const blocks = useMemo(() => splitBlocks(text), [text]);
  return (
    <>
      {blocks.map((b, i) => {
        const tail = streaming && i === blocks.length - 1;
        // Абзац ответа — по месту: ответ только дописывается, абзац N остаётся абзацем N.
        // biome-ignore lint/suspicious/noArrayIndexKey: место абзаца — его постоянный ключ
        return <Block key={i} text={tail ? remend(b, { linkMode: 'text-only' }) : b} />;
      })}
    </>
  );
}
